<script>
import IconX from "@js/components/Icons/IconX.vue";
import IconPhone from "@js/components/Icons/IconPhone.vue";
import {Link} from "@inertiajs/vue3";
import IconShare from "@js/components/Icons/IconShare.vue";
import IconWhatsApp from "@js/components/Icons/IconWhatsApp.vue";
import IconTelegram from "@js/components/Icons/IconTelegram.vue";
import IconChatFilled from "../Icons/IconChatFilled.vue";

import {useCallbackStore} from "@js/store/CallbackStore.js";
import {useThemeStore} from "@js/store/ThemeStore.js";
import {useJivoStore} from "@js/store/JivoStore.js";
import {contacts} from "@js/company_details.ts";
import {checkDocument} from "@js/helpers.ts";
import IconMaxGradient from "@js/components/Icons/IconMaxInner.vue";
import IconMax from "@js/components/Icons/IconMax.vue";

export default {
    name: "SpeedDial",
    components: {IconMax, IconMaxGradient, IconChatFilled, IconTelegram, IconWhatsApp, IconShare, IconPhone, IconX, Link},
    data() {
        return {
            icon_cycles: {chat: 1, whatsapp: 2, telegram: 3, max:4, callback: 5},
            active_icon: 1,
            interval: null,
            isOpen: false,
            isHoverCapable: false,
        };
    },
    methods: {
        share() {
            if (checkDocument() && typeof navigator.share !== "undefined") {
                navigator
                    .share({
                        title: this.$page?.props?.title ?? document.title,
                        text: this.$page?.props?.description ?? "",
                        url: window.location.href,
                    })
                    .catch(() => {
                    });
            } else {
                try {
                    navigator.clipboard?.writeText(window.location.href);
                } catch {
                }
            }
        },
        toggle() {
            this.isOpen = !this.isOpen;
        },
        open() {
            this.isOpen = true;
        },
        close() {
            this.isOpen = false;
        },
        onKeydown(e) {
            if (e.key === "Escape") this.close();
        },
        _outsideClick(e) {
            if (!this.$el.contains(e.target)) this.close();
        },
    },
    mounted() {
        // hover-детект для десктопов
        try {
            this.isHoverCapable = window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches ?? false;
        } catch {
        }

        // Ротация иконки
        this.interval = setInterval(() => {
            const cycleValues = Object.values(this.icon_cycles);
            const idx = cycleValues.indexOf(this.active_icon);
            this.active_icon = cycleValues[(idx + 1) % cycleValues.length];
        }, 4000);

        // Закрытие по Esc и клику вне
        document.addEventListener("keydown", this.onKeydown);
        document.addEventListener("click", this._outsideClick, {capture: true});
    },
    beforeUnmount() {
        clearInterval(this.interval);
        document.removeEventListener("keydown", this.onKeydown);
        document.removeEventListener("click", this._outsideClick, {capture: true});
    },
    setup() {
        const callbackStore = useCallbackStore();
        const themeStore = useThemeStore();
        const jivoStore = useJivoStore();
        return {callbackStore, themeStore, jivoStore, contacts};
    },
};
</script>

<template>
    <div class="group fixed end-6 bottom-6 z-[60]" @mouseleave="isHoverCapable && close()">
        <!-- Меню -->
        <div id="speed-dial-menu"
             class="flex flex-col items-center  space-y-2 transition-opacity duration-200"
             :class="isOpen ? 'opacity-100 pointer-events-auto mb-4' : 'opacity-0 pointer-events-none h-0 m-0'"
             :aria-hidden="!isOpen"
             role="menu">
            <!-- Поделиться -->
            <div class="rel">
                <button @click.prevent="share" aria-label="Поделиться" class="btn-52 tt"
                        data-tooltip="Поделиться" role="menuitem" :tabindex="isOpen ? 0 : -1">
                    <IconShare class="w-5 h-5"/>
                </button>
            </div>

            <!-- WhatsApp -->
            <div class="rel">
                <a :href="contacts.whatsapp" target="_blank" aria-label="WhatsApp" class="btn-52 tt"
                   data-tooltip="WhatsApp" role="menuitem" :tabindex="isOpen ? 0 : -1">
                    <IconWhatsApp class="w-6 h-6 text-whatsapp"/>
                </a>
            </div>

            <!-- Telegram -->
            <div class="rel">
                <a :href="contacts.telegram" target="_blank" aria-label="Telegram" class="btn-52 tt"
                   data-tooltip="Telegram" role="menuitem" :tabindex="isOpen ? 0 : -1">
                    <IconTelegram class="w-5 h-5 -ms-0.5 text-telegram"/>
                </a>
            </div>

            <!-- Max -->
            <div class="rel">
                <a :href="contacts.max" target="_blank" aria-label="Max" class="btn-52 tt"
                   data-tooltip="Max" role="menuitem" :tabindex="isOpen ? 0 : -1">
                    <IconMaxGradient class="w-6 h-6"/>
                </a>
            </div>

            <!-- Обратный звонок -->
            <div class="rel">
                <button @click.prevent="callbackStore.showBaseCallbackPopup" aria-label="Обратный звонок"
                        class="btn-52 tt" data-tooltip="Обратный звонок"
                        role="menuitem" :tabindex="isOpen ? 0 : -1">
                    <IconPhone class="w-7 h-7 text-gray-700 group-hover:text-gray-900"/>
                </button>
            </div>

            <!-- Онлайн-чат -->
            <div class="rel">
                <button @click.prevent="jivoStore.openJivoChat" aria-label="Онлайн-чат"
                        class="btn-52 tt" data-tooltip="Онлайн-чат"
                        role="menuitem" :tabindex="isOpen ? 0 : -1">
                    <IconChatFilled class="w-6 h-6 text-gray-700 group-hover:text-gray-900"/>
                    <span class="abs-dot" v-if="jivoStore.hasUnreadMessages">
                        <span class="dot-wrap">
                            <span class="dot-ping"></span>
                            <span class="dot-core"></span>
                        </span>
                    </span>
                </button>
            </div>
        </div>

        <!-- Триггер -->
        <button @mouseenter="isHoverCapable && open()"
                type="button"
                aria-controls="speed-dial-menu"
                :aria-expanded="isOpen ? 'true' : 'false'"
                aria-haspopup="menu"
                aria-label="Быстрое меню"
                @click.prevent="toggle"
                :class="{
                          'bg-whatsapp': active_icon === icon_cycles.whatsapp,
                          'bg-telegram': active_icon === icon_cycles.telegram,
                          'bg-baseColor': active_icon === icon_cycles.chat || active_icon === icon_cycles.callback,
                          'max-gradient': active_icon === icon_cycles.max,
                        }"
                class="relative flex items-center justify-center rounded-full w-14 h-14 focus:outline-none transition">
            <transition name="icon-fade" mode="out-in">
                <IconWhatsApp v-if="active_icon === icon_cycles.whatsapp" :key="'sd_'+icon_cycles.whatsapp"
                              class="w-6 h-6 text-white"/>
                <IconTelegram v-else-if="active_icon === icon_cycles.telegram" :key="'sd_'+icon_cycles.telegram"
                              class="w-5 h-5 -ms-0.5 text-white"/>
                <IconMax v-else-if="active_icon === icon_cycles.max" :key="'sd_'+icon_cycles.max"
                              class="w-6 h-6 text-white"/>
                <IconChatFilled v-else-if="active_icon === icon_cycles.chat" :key="'sd_'+icon_cycles.chat"
                                class="w-6 h-6 -ms-0.5 text-white"/>
                <IconPhone v-else :key="'sd_'+icon_cycles.callback" class="w-7 h-7 text-white"/>
            </transition>

            <span class="abs-dot" v-if="jivoStore.hasUnreadMessages">
                <span class="dot-wrap">
                    <span class="dot-ping"></span>
                    <span class="dot-core"></span>
                </span>
            </span>
        </button>
    </div>
</template>

<style scoped lang="scss">
/* Базовые утилиты */
.rel {
    position: relative;
}

.abs-dot {
    position: absolute;
    top: 0;
    inset-inline-end: .25rem;
}

.dot-wrap {
    position: relative;
    display: inline-flex;
    width: .75rem;
    height: .75rem;
}

.dot-ping {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    //background: var(--nav-hover, theme(colors.navHoverColor));
    animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
    opacity: .75;
}

.dot-core {
    position: relative;
    display: inline-flex;
    width: .75rem;
    height: .75rem;
    border-radius: 9999px;
    //background: var(--nav-hover, theme(colors.navHoverColor));
}

@keyframes ping {
    75%, 100% {
        transform: scale(2);
        opacity: 0;
    }
}

/* Кнопка 52×52 */
.btn-52 {
    @apply flex justify-center items-center w-[52px] h-[52px] bg-white rounded-full
    border border-gray-200 shadow-sm hover:bg-gray-50 focus:ring-4 focus:ring-gray-300 focus:outline-none;
}

/* Иконка-переключатель (анимация) */
.icon-fade-enter-active,
.icon-fade-leave-active {
    transition: opacity .4s ease, transform .4s ease;
}

.icon-fade-enter-from,
.icon-fade-leave-to {
    opacity: 0;
    transform: scale(.8);
}

.icon-fade-enter-to,
.icon-fade-leave-from {
    opacity: 1;
    transform: scale(1);
}

/* CSS-only Tooltip */
@media (hover: hover) and (pointer: fine) {
    .tt {
        position: relative;
    }

    .tt::after {
        content: attr(data-tooltip);
        position: absolute;
        right: calc(100% + .5rem);
        top: 50%;
        transform: translate(-4px, -50%);
        background: #3a4250;
        color: #fff;
        font-size: 0.875rem; /* text-sm */
        line-height: 1.25rem;
        padding: .375rem .625rem;
        border-radius: .5rem;
        white-space: nowrap;
        box-shadow: 0 4px 10px rgba(0, 0, 0, .15);
        opacity: 0;
        pointer-events: none;
        transition: opacity .15s ease, transform .15s ease;
        z-index: 70;
    }

    .tt::before {
        content: '';
        position: absolute;
        right: calc(100% + .25rem);
        top: 50%;
        transform: translateY(-50%);
        border: 6px solid transparent;
        border-right: 0;
        border-left-color: #3a4250;
        opacity: 0;
        transition: opacity .15s ease;
        z-index: 71;
    }

    .tt:hover::after,
    .tt:focus-visible::after,
    .tt:hover::before,
    .tt:focus-visible::before {
        opacity: 1;
        transform: translate(0, -50%);
    }

    @media (prefers-reduced-motion: reduce) {
        .tt::after {
            transition: opacity .001s linear, transform .001s linear;
        }
        .tt::before {
            transition: opacity .001s linear;
        }
    }
}
</style>
